<template>
  <component
    :is="tag"
    ref="overlay"
    v-tooltip="tooltip"
    :class="overlayClasses"
    :d="d"
    :points="points"
    fill="url(#diagonalHatch)"
    @click="proceedClick"
  />
</template>

<script>
import { mapGetters } from 'vuex';
import { pricesAttributes } from '@/utils/attribute-prices';
import { attrKey2Label } from '@/utils/attribute-labels';
import { cdnUrl } from '@/utils/urls';

import 'floating-vue/dist/style.css'

export default {
  name: 'ViewportOverlay',
  props: {
    overlay: {
      type: Object,
      required: true,
    },
    overlayStatus: {
      type: Boolean,
      required: true,
    },
    selectedOverlay: {
      type: Number,
      required: false,
    },
    tooltipDisabled: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({ clicked: false }),
  computed: {
    ...mapGetters('units', ['unitById', 'units']),
    ...mapGetters('unitTypes', ['unitTypeById']),
    ...mapGetters(
      'project',
      [
        'statuses',
        'attributes',
        'i18nTranslations',
        'showExternalFloorPlan',
        'floorPlanOnTooltipShowing',
        'floorPlan2DFirstShowing',
      ],
    ),
    ...mapGetters('exteriors', ['exteriors']),
    isPath() {
      return this.overlay.value_type === 'path';
    },
    tag() {
      return this.isPath ? 'path' : 'polygon';
    },
    ensureFormatted() {
      const { value } = this.overlay;

      return value && value.startsWith('((') ? value.slice(2, value.length - 2) : value;
    },
    d() {
      return this.isPath ? this.ensureFormatted : undefined;
    },
    points() {
      return !this.isPath ? this.ensureFormatted : undefined;
    },
    overlayColorClass() {
      if (this.isCustomColored) {
        return 'color_status_custom';
      }

      if (
        !this.units.length || !this.statuses.length || (this.overlay.color && this.overlay.color !== 'color_status_d')
      ) {
        return this.overlay.color;
      }

      if (this.status) {
        return this.status.color_class;
      }

      const statusAvailable = this.statuses.find(status => status.internal_slug === 'available');
      const statusBooked = this.statuses.find(status => status.internal_slug === 'booked');
      const statusSold = this.statuses.find(status => status.internal_slug === 'sold');
      const exteriorUnits = this.units.filter(unit => (unit.exteriors || []).includes(this.overlay.linked_exterior_id));
      const unitsAvailable = exteriorUnits.find(unit => unit.status_id === statusAvailable.status_id);
      const unitsBooked = exteriorUnits.find(unit => unit.status_id === statusBooked.status_id);
      const unitsSold = exteriorUnits.find(unit => unit.status_id === statusSold.status_id);

      if (unitsAvailable) {
        return statusAvailable.color_class;
      } else if (unitsBooked) {
        return statusBooked.color_class;
      } else if (unitsSold) {
        return statusSold.color_class;
      }

      return statusSold.color_class;
    },
    tooltip() {
      return {
        content: this.getTooltipContent.bind(this),
        html: true,
        distance: 10,
        container: 'body',
        instantMove: true,
        strategy: 'absolute',
        flip: true,
        shift: true,
        themes: {
          tooltip: {
            placement: 'auto',
            delay: {
              show: 0,
              hide: 0,
            },
            html: true,
            triggers: ['hover', 'focus', 'touch'],
            loadingContent: `<i>${this.$t('Loading_')}</i>`,
          },
        }
      };
    },
    overlayClasses() {
      const selected = this.selectedOverlay === this.overlay.overlay_id;

      return [
        {
          'selected-overlay': selected,
          'display-status': this.overlayStatus,
        },
        this.overlayColorClass,
      ];
    },
    isCustomColored() {
      return !!this.overlayColorHex;
    },
    status() {
      return this.statuses.find(status => status.status_id === this.overlay.status_id);
    },
    overlayColorHex() {
      return this.status ? this.status.color : null;
    },
    exterior() {
      return this.exteriors.find(ext => ext.exterior_id === this.overlay.linked_exterior_id);
    },
    unit() {
      return this.overlay.unit_id && this.unitById(this.overlay.unit_id);
    },
    floorPlans() {
      if (!this.unit) {
        return [];
      }

      if (this.showExternalFloorPlan) {
        return [
          {
            filepath: this.unit.ext_floor_plan_url || '',
            type: '3D',
            external: true,
          }];
      }

      const unitType = this.unitTypeById(this.unit.unit_type_id);
      const interiors = this.unit.interiors.concat(unitType.interiors);

      const [less, more] = this.floorPlan2DFirstShowing ? [-1, 1] : [1, -1];

      return Array
        .from(unitType.floorplans)
        .sort((fp1, fp2) => (fp1.type < fp2.type
            ? less
            : fp1.type > fp2.type
              ? more
              : 0
        ))
        .map(floorplan => ({
          ...floorplan,
          caption: `Floor ${floorplan.floor} of ${unitType.attributes.floors_internal}`,
        }))
        .concat(interiors.map(interior => ({
          filepath: interior.images[0],
          type: this.$t('unit_slides_additional'),
          ...interior,
        })));
    },
    floorplanHref() {
      const floorplan = this.floorPlans.find(fp => fp.type === '2D') || this.floorPlans[0];

      return floorplan && floorplan.filepath ? `${cdnUrl(floorplan.filepath)}?width=165&height=999` : '';
    },
  },
  watch: {
    selectedOverlay() {
      this.clicked = false; // unflag the state
    },
    isCustomColored() {
      this.initColorCustom();
    },
  },
  mounted() {
    this.initColorCustom();
  },
  methods: {
    initColorCustom() {
      if (!this.$el) {
        return;
      }

      if (this.isCustomColored) {
        this.$el.style.setProperty('--color-status-custom', this.hexToRgb(this.overlayColorHex));
      } else {
        this.$el.style.removeProperty('--color-status-custom');
      }
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

      return result
        ? result.slice(1, 4)
          .map(v => parseInt(v, 16))
          .join(', ')
        : null;
    },
    getTooltipContent() {
      const { overlay } = this;

      if (overlay.link_active && !!overlay.url) {
        return `<h3>${overlay.label}</h3>`;
      } else if (overlay.linked_exterior_id) {
        return `<h3>${this.exterior.name || 'Exterior'}</h3>`;
      } else if (overlay.unit_id) {
        const unit = this.unitById(overlay.unit_id);

        if (!unit) {
          return `<i>${this.$t('Loading_')}</i>`;
        }

        const badgeStyleAttr = this.overlayColorHex ? `style="--color-status-custom: ${this.overlayColorHex}"` : '';
        const unitAttrData = this.status.display_attributes ? this.renderUnitAttrData(unit) : '';
        const contentClass = ['overlay-tooltip', this.overlayColorClass]
          .concat(this.unit && this.unit.special_setting === 'style_premium' ? 'premium' : [])
          .concat(this.floorPlanOnTooltipShowing && this.status && this.status.flp_preview ? 'with-floorplan' : []).join(" ");
        return `
          <div class="${contentClass}">
            <div class="overlay-layout">
              <div class="floorplan">
                ${!this.tooltipDisabled && this.floorplanHref ? `<img src="${this.floorplanHref}">` : ''}
              </div>
              <div class="data">
                <div class="unit-title mb-2">
                  <h3 class="overlay-tooltip__unit-title">${unit.name}</h3>
                  <span ${badgeStyleAttr} class="unit-status-badge ml-2 ${this.overlayColorClass}">
                    ${this.$t(this.status.name || this.status.internal_slug)}
                  </span>
                </div>
                <div class="attrs">${unitAttrData}</div>
              </div>
            </div>
          </div>
          `;
      }

      return '';
    },
    renderUnitAttrData(unit) {
      const unitType = this.unitTypeById(unit.unit_type_id);
      const attributesMeta = Object
        .keys(unit.attributes)
        .concat(Object.keys(unitType.attributes));

      return attributesMeta
        .map((key) => {
          if (pricesAttributes.includes(key)) {
            const status = this.statuses.find(status => status.status_id === unit.status.status_id);

            if (!status.display_price) {
              return false;
            }
          }

          const source = [
            unit.formatted_attributes,
            unitType.formatted_attributes,
            unitType.attributes,
          ].find(source => source[key] != null);

          if (source) {
            return `<div class="attr ${key}"><span class="attr-name">${this.$t(attrKey2Label[key])}:</span> <span class="attr-value">${source[key]}</span></div>`;
          }

          return false;
        })
        .filter(v => v)
        .join('');
    },
    proceedClick() {
      this.clicked = true; // flag the state for Viewport component
      this.$emit('toggleOverlay', this.overlay);
    },
  },
};
</script>

<style lang="scss">
.v-popper--theme-tooltip .v-popper__inner {
  font-family: $body-font-family;
  font-size: var(--viewport-overlay-font-size, 0.94em);
  line-height: var(--viewport-overlay-line-height, 1.3em);
  background-color: var(--viewport-overlay-background-color, #fff);
  color: var(--viewport-overlay-tooltip-color, rgba(0, 0, 0, .87));
  padding: var(--viewport-overlay-padding, 8px 10px);
  border-radius: var(--viewport-overlay-tooltip-border-radius, 4px);
  filter: var(--viewport-overlay-tooltip-shadow, drop-shadow(0px 0px 4px #bbb));
}
.v-popper--theme-tooltip .v-popper__arrow-inner {
  visibility: hidden;
}

.v-popper--theme-tooltip .v-popper__arrow-outer {
  visibility: hidden;
}
.overlay-tooltip {
  &__unit-title {
    font-size: 1.3em;
    font-weight: 500;
    display: inline-block;
  }

  .attrs {
    display: flex;
    flex-direction: column;
  }

  &.premium {
    background-color: var(--profile-color-2, var(--viewport-overlay-background-color, #fff));
  }

  &:not(.with-floorplan) .floorplan {
    display: none;
  }
}

.overlay-status-switch {
  position: fixed;
  bottom: var(--app-main-padding);
  left: var(--app-main-padding);
  max-height: 56px;
  border-radius: 28px;
  padding: 0 !important;
  margin: 0 !important;

  .v-input__slot {
    border-radius: 28px;
    padding-left: 18px;
    padding-right: 18px;

    .v-label {
      font-size: 0.875em;
      font-weight: 700;
    }
  }
}

.unit-status-badge {
  display: inline-block;
  padding: 1px 4px;
  font-size: 0.875em;
  font-weight: 500;
  color: #fff;
  border-radius: 2px;
  background-color: var(--overlay-status-color);
}

.unit-status-badge.color_status_a {
  --overlay-status-color: rgba(var(--color-status-a), 1.0);
}

.unit-status-badge.color_status_b {
  --overlay-status-color: rgba(var(--color-status-b), 1.0);
}

.unit-status-badge.color_status_c {
  --overlay-status-color: rgba(var(--color-status-c), 1.0);
}

.unit-status-badge.color_status_d {
  --overlay-status-color: rgba(var(--color-status-d), 1.0);
}

.unit-status-badge.color_status_custom {
  --overlay-status-color: var(--color-status-custom);
}

.tooltip[x-placement^="top"] {
  z-index: 20;
}

.overlay-layout {
  display: flex;
  flex-flow: row nowrap;
  gap: 8px;

  .floorplan {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 6px;

    img {
      max-width: 200px;
      max-height: 250px;
    }
  }

  .unit-title {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 8px;
  }
}
</style>

<style lang="scss" scoped>
path, polygon {
  fill: transparent;
  stroke: transparent;
  cursor: pointer !important;
  pointer-events: visible;
  stroke-width: 2px;
  stroke-linecap: round;
  transition: all .2s ease;
  outline: none;
}

$variants: 'a' 'b' 'c' 'd' 'custom';

@each $variant in $variants {
  path.color_status_#{$variant}.display-status, polygon.color_status_#{$variant}.display-status {
    fill: rgba(var(--color-status-#{$variant}), var(--fill-opacity));
    stroke: rgba(var(--color-status-#{$variant}), var(--stroke-opacity));
  }

  path.color_status_#{$variant}:hover, polygon.color_status_#{$variant}:hover {
    fill: rgba(var(--color-status-#{$variant}), var(--hover-fill-opacity));
    stroke: rgba(var(--color-status-#{$variant}), var(--hover-stroke-opacity));
  }

  path.color_status_#{$variant}.selected-overlay, polygon.color_status_#{$variant}.selected-overlay {
    fill: rgba(var(--color-status-#{$variant}), var(--fill-opacity));
    stroke: rgba(255, 255, 255, var(--stroke-opacity));
    stroke-width: 4px;
  }
}
</style>
